/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useEffect, useMemo, ReactNode, RefObject, useRef } from 'react';
import { Grid, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useTable,
  useFilters,
  usePagination,
  useGlobalFilter,
  Column,
  Row,
  HeaderGroup,
  useSortBy,
  useExpanded,
  HeaderProps
} from 'react-table';
import {
  ColumnLevelFilterButton,
  getConditionalSelectHeaderCheckboxProps,
  GlobalFilter,
  globalFilterForSubRows,
  TableFilterByColumn,
  TableRefereshButton
} from 'utils/react-table';
import ScrollX from 'components/ScrollX';
import TableSkeleton from 'components/skeletons/TableSkeleton';
import OverflowText from './OverflowText';
import { uniqBy } from 'lodash';
import { useRowSelect } from 'react-table';
import { FloatDeleteButton, IndeterminateCheckbox } from 'components/third-party/ReactTable';
import { Divider } from '@mui/material';
import { OnPaginationChangeFun } from 'types/table';
import { Checkbox } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { useUserSettings } from 'hooks/useUserSettings';
interface TableWrapperProps {
  columns: Column<any>[];
  data: any[];
  title?: string | ReactNode;
  createButton: React.ReactNode;
  showGlobalFilter: boolean;
  showPagination: boolean;
  loading?: boolean;
  emptyTableMessage?: string;
  isSelectBox?: boolean;
  selectBoxModule?: any;
  subHeading?: string;
  children: (props: {
    getTableProps: () => any;
    getTableBodyProps: () => any;
    headerGroups: HeaderGroup<any>[];
    page: Row<any>[]; // Pass 'page' instead of 'rows'
    prepareRow: (row: Row<any>) => void;
    expandedColumns: Set<string>;
    toggleColumn: (accessor: string) => void;
    isMobile: boolean;
    getVisibleItems: (items: any[]) => any[];
    tableRef: RefObject<HTMLTableElement>;
    emptyTableMessage: string;
    gotoPage: (page: number) => void;
    pageCount: number;
    pageIndex: number;
    pageSize: number;
    setPageSize: (size: number) => void;
    showColumnLevelFilterInputBox: boolean;
    internalSelectedRowIds?: Record<number, boolean>;
    toggleAllRowsSelected: any;
    floatDeleteButton?: ReactNode;
    floatButtonRowIndex?: number | null;
  }) => ReactNode;
  showRefreshButton?: boolean;
  onRefreshClick?: () => void;
  handleSelectAllChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultipleFileLockRelease?: () => void;
  showCustomFilterColumnButton?: boolean;
  customFilterColumns?: string[];
  showColumnLevelFilter?: boolean;
  isExpandable?: boolean;
  showTitleTwo?: any;
  titleTwo?: any;
  currentPage?: number;
  selectedRowIds?: Record<number, boolean>;
  showCheckbox?: boolean;
  showLockCheckbox?: boolean;
  isChecked?: boolean;
  showUnlockIcon?: boolean;
  onRecordsUpdate?: (recordsData: any[]) => void; // Callback to pass updated records
  showCheckboxInSubRows?: boolean;
  expandedRowIds?: Record<number, boolean> | undefined;
  prefixElement?: ReactNode | undefined;
  onPaginationChange?: OnPaginationChangeFun;
  getVisibleItem?: any;
  onClickMultiDelete?: (_data: any[]) => Promise<any>;
  autoResetSelectedRows?: boolean;
  showGloableCheckbox?: boolean;
  floatButtonColumnNumber?: number;
  importPagination?: boolean;
  tableMaxHeight?: number | string;
  shouldClearSelectionOnChangeFilter?: boolean;
  isImageViewer?: boolean;
}

const Wrapper = styled('div')(({ theme }) => ({
  '.header': {
    position: 'sticky',
    zIndex: 1,
    width: 'fit-content'
  },
  '& th[data-sticky-td]': {
    position: 'sticky',
    zIndex: '5 !important'
  },
  '.MuiTableFooter-root': {
    position: 'sticky',
    bottom: 0,
    background: theme.palette.background.paper
  }
}));

const TableWrapper: React.FC<TableWrapperProps> = ({
  columns,
  data,
  title,
  createButton,
  showGlobalFilter,
  showPagination,
  loading = false,
  emptyTableMessage = 'No File Found',
  isSelectBox = false,
  selectBoxModule = () => {},
  showRefreshButton,
  onRefreshClick = () => {},
  handleSelectAllChange = () => {},
  handleMultipleFileLockRelease = () => {},
  showCustomFilterColumnButton = false,
  customFilterColumns = [],
  showColumnLevelFilter = false,
  isExpandable = false,
  showTitleTwo = false,
  titleTwo = {},
  subHeading = '',
  children,
  currentPage = 0,
  selectedRowIds = {},
  showCheckbox,
  showLockCheckbox = false,
  isChecked = false,
  showUnlockIcon = false,
  onRecordsUpdate = () => null,
  showCheckboxInSubRows = false,
  expandedRowIds,
  prefixElement,
  onPaginationChange,
  getVisibleItem = true,
  onClickMultiDelete,
  autoResetSelectedRows = true,
  showGloableCheckbox,
  floatButtonColumnNumber,
  importPagination,
  tableMaxHeight = 650,
  shouldClearSelectionOnChangeFilter,
  isImageViewer = false
}) => {
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [availableWidth, setAvailableWidth] = useState(0);
  const [showColumnLevelFilterInputBox, setShowColumnLevelFilterInputBox] = useState<boolean>(false);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const translate = (key: string, values?: any) => (window as any).translate(key, values);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { userSettings } = useUserSettings();
  const updateAvailableWidth = () => {
    if (tableRef.current) {
      setAvailableWidth(tableRef.current.clientWidth);
    }
  };
  const [rowSelectionHistory, setRowSelectionHistory] = useState<number[]>([]);

  useEffect(() => {
    if (getVisibleItem) {
      updateAvailableWidth();
      window.addEventListener('resize', updateAvailableWidth);
      return () => {
        window.removeEventListener('resize', updateAvailableWidth);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getVisibleItem) {
      updateAvailableWidth();
    }
  }, [loading, data, getVisibleItem]);
  const toggleColumn = (accessor: string) => {
    setExpandedColumns((prevExpandedColumns) => {
      const newExpandedColumns = new Set(prevExpandedColumns);
      if (newExpandedColumns.has(accessor)) {
        newExpandedColumns.delete(accessor);
      } else {
        newExpandedColumns.add(accessor);
      }
      return newExpandedColumns;
    });
  };

  const defaultColumn = useMemo(
    () => ({
      minWidth: 50,
      width: 100,
      maxWidth: 400,
      Cell: OverflowText
    }),
    []
  );

  const handleRowSelectionHistory = (index: number, isChecked: boolean) => {
    setRowSelectionHistory((rowSelectionHistory) => {
      let selectedRowsIndex = [...rowSelectionHistory];
      if (isChecked) {
        selectedRowsIndex.push(index);
      } else {
        selectedRowsIndex = selectedRowsIndex.filter((rowIndex) => rowIndex !== index);
      }
      return selectedRowsIndex;
    });
  };

  const customColumnHooks = useMemo(() => {
    const hooks: any[] = [];
    if (showCheckbox) {
      hooks.push({
        id: 'row-selection-chk',
        accessor: 'Selection',
        disableSortBy: true,
        className: 'row-selection-chk-TableC',
        width: 40, // Set a specific width
        maxWidth: 50, // Set a maximum width
        ...(showGloableCheckbox
          ? {
              Header: (props: HeaderProps<any>) => {
                const checkboxProps = getConditionalSelectHeaderCheckboxProps({
                  headerProps: props,
                  checkIfRowIsSelectable: (row) => !row.original.disableCheckBox,
                  shouldSelectPage: false
                });
                return <IndeterminateCheckbox {...checkboxProps} />;
              }
            }
          : {}),
        Cell: ({ row }: { row: Row<any> }) => {
          const props = row.getToggleRowSelectedProps();
          return (
            <IndeterminateCheckbox
              {...props}
              showCheckboxInSubRows={Boolean(showCheckboxInSubRows)}
              isMainRow={Boolean(row.depth === 0)}
              onChange={(e: any) => {
                if (props.onChange) {
                  props.onChange(e);
                }
                handleRowSelectionHistory(row.index, e.target.checked);
              }}
              disabled={row?.original?.disableCheckBox}
            />
          );
        }
      });
    }
    return hooks;
  }, [showCheckbox, showCheckboxInSubRows, showGloableCheckbox]);
  // const sorting = columns
  //   .filter((col) => col.defaultSortOrder)
  //   .map((col) => ({
  //     id: col.accessor,
  //     desc: col.defaultSortOrder === 'desc'
  //   }));

  const userPageSize = useMemo(() => {
    const size = Number(userSettings.DefaultRecordsPerPage);
    return !isNaN(size) && size > 0 ? size : 10;
  }, [userSettings.DefaultRecordsPerPage]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    gotoPage,
    preGlobalFilteredRows,
    setPageSize,
    page,
    pageCount,
    setGlobalFilter,
    state,
    preGlobalFilteredFlatRows,
    setFilter,
    toggleAllRowsSelected,
    rows
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableSortRemove: true, // prevents resetting to original order during sort.
      initialState: {
        pageIndex: currentPage,
        pageSize: userPageSize,
        selectedRowIds,
        expanded: expandedRowIds || {}
        // sortBy: sorting
      },
      ...(isExpandable ? { globalFilter: globalFilterForSubRows } : {}),
      autoResetPage: false,
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetExpanded: Boolean(expandedRowIds), // disable auto reset expanded row if expanded is handled manually
      paginateExpandedRows: false,
      autoResetSelectedRows,
      autoResetGlobalFilter: false
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.allColumns.push((columns: Column[]) => [...customColumnHooks, ...columns]);
    }
  );

  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange({ totalRecords: data.length, pageIndex: state.pageIndex, pageSize: state.pageSize });
    }
    if (
      // Number(userSettings?.DefaultRecordsPerPage) === Number(data?.length) &&
      Number(data?.length) === Number(state?.pageSize)
    ) {
      gotoPage(0);
    }
  }, [data.length, onPaginationChange, state.pageIndex, state.pageSize]);

  useEffect(() => {
    if (userPageSize) {
      setPageSize(userPageSize);
      gotoPage(0); // optional: reset to first page
    }
  }, [userPageSize, setPageSize, gotoPage]);

  // const getVisibleItems = (items: any[]) => {
  //   const itemWidth = 400; // Estimated width of each item including margin
  //   const itemsThatFit = Math.floor((availableWidth - 200) / itemWidth); // Subtracting 200 to account for other cell content
  //   return items.slice(0, itemsThatFit);
  // };
  const getVisibleItems = useMemo(() => {
    if (getVisibleItem) {
      return (items: any[]) => {
        const itemWidth = 400;
        const itemsThatFit = Math.floor((availableWidth - 200) / itemWidth);
        return items.slice(0, itemsThatFit);
      };
    } else {
      return (items: any[]) => items;
    }
  }, [getVisibleItem, availableWidth]);

  const uniqueValuesByColumns: { columnId: string; value: string }[] = useMemo(() => {
    let filteredColumns: { columnId: string; value: string }[] = [];
    if (showCustomFilterColumnButton) {
      preGlobalFilteredFlatRows.forEach(({ original }: any) => {
        customFilterColumns.forEach((columnId: string) => {
          if (original[columnId]) {
            filteredColumns.push({ columnId, value: original[columnId] });
          }
        });
      });
    }
    return uniqBy(filteredColumns, 'value');
  }, [preGlobalFilteredFlatRows, customFilterColumns, showCustomFilterColumnButton]);

  useEffect(() => {
    if (showCheckbox) {
      // Send back updated records with selection status
      let updatedRecords = data.map((record, index) => ({
        ...record,
        isSelected: !!rows.find((row) => Number(row.id) === index) && !!state.selectedRowIds[index]
      }));
      if (JSON.stringify(data) !== JSON.stringify(updatedRecords)) onRecordsUpdate(updatedRecords);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedRowIds, showCheckbox]);

  useEffect(() => {
    // Send back updated records with expand status
    const updatedRecords = data.map((record, index) => ({
      ...record,
      isExpanded: !!state.expanded[index]
    }));
    if (JSON.stringify(data) !== JSON.stringify(updatedRecords)) onRecordsUpdate(updatedRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.expanded]);

  const totalSelectedRows = useMemo(() => Object.keys(state?.selectedRowIds || {}).length, [state?.selectedRowIds]);
  const tableContainerRef = useRef(null);

  const floatButtonRowIndex: number | null = useMemo(() => {
    if (totalSelectedRows === data.length) {
      return 1;
    } else if (rowSelectionHistory.length > 0) {
      return rowSelectionHistory[rowSelectionHistory.length - 1];
    }
    return null;
  }, [totalSelectedRows, data.length, rowSelectionHistory]);

  const floatDeleteButton = useMemo(
    () => {
      const selectedRowsIndex: number[] = Object.keys(state?.selectedRowIds || {}).map((key) => Number(key));
      const selectedData = data.filter((_row, index) => selectedRowsIndex.includes(index));
      if (totalSelectedRows === 0) {
        return null;
      }
      return floatButtonColumnNumber !== undefined && floatButtonColumnNumber >= 0 ? (
        <FloatDeleteButton
          totalSelectedRows={totalSelectedRows}
          tableContainerRef={tableContainerRef}
          floatButtonColumnNumber={floatButtonColumnNumber}
          onClose={() => {
            setRowSelectionHistory([]);
            toggleAllRowsSelected(false);
          }}
          selectedRows={selectedData}
          onClickMultiDelete={onClickMultiDelete}
          isSelectedAll={totalSelectedRows === data.length}
        />
      ) : (
        <></>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalSelectedRows, tableContainerRef, floatButtonColumnNumber, state?.selectedRowIds, data]
  );
  const wrapName = (name?: string, maxLength = 15) => {
    if (!name) return '';

    const value = String(name);
    return value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
  };

  useEffect(() => {
    if (totalSelectedRows === 0 && floatButtonRowIndex !== null) {
      setRowSelectionHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalSelectedRows]);

  const unSelectAllRows = () => shouldClearSelectionOnChangeFilter && onRecordsUpdate(data.map((row) => ({ ...row, isSelected: false })));

  return (
    <>
      <Grid container ref={tableContainerRef}>
        <Grid container className="pb-15" alignItems={'center'}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4">{title}</Typography>
            {showLockCheckbox && (
              <Grid container alignItems="center" spacing={2}>
                {/* Checkbox with label */}
                <Grid item>
                  <Tooltip
                    title="Select 1 or more files to unlock. You can unlock files if they are in below status only.
•	Exported
•	Verified
•	QA in progress
"
                  >
                    <FormControlLabel
                      control={<Checkbox checked={isChecked} onChange={handleSelectAllChange} />}
                      label={`${translate('pages.review.assurance.labels.selectAll')}`}
                    />
                  </Tooltip>
                </Grid>

                {/* Vertical separator */}
                {showUnlockIcon && <Divider orientation="vertical" flexItem sx={{ height: 50, marginTop: 2 }} />}

                {/* Lock icon */}
                <Grid item>
                  {showUnlockIcon && (
                    <>
                      <Tooltip title={'Click to unlock the selected files'}>
                        <LockOpenOutlinedIcon onClick={handleMultipleFileLockRelease} style={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
            <Grid item xs={5} md={5}>
              {showTitleTwo && (
                <Stack
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection="row"
                  sx={{ marginTop: '1em', marginBottom: '0.5em' }}
                >
                  <Stack flexDirection="column">
                    <Typography mb={0.8}>Process</Typography>
                    <Tooltip title={titleTwo?.processName || ''} arrow placement="top">
                      <Typography mb={0.8} variant="h5">
                        {wrapName(titleTwo?.processName)}
                      </Typography>
                    </Tooltip>
                  </Stack>

                  <Stack flexDirection="column">
                    <Typography mb={0.8}>Document Type</Typography>
                    <Tooltip title={titleTwo?.docTypeName || ''} arrow placement="top">
                      <Typography variant="h5">{wrapName(titleTwo?.docTypeName)}</Typography>
                    </Tooltip>
                  </Stack>
                </Stack>
              )}
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: { xs: 1 }
            }}
            gap={2}
          >
            {isSelectBox && selectBoxModule()}

            <Stack direction="row" spacing={3} alignItems="center" divider={<Divider orientation={'vertical'} flexItem />}>
              {prefixElement && <>{prefixElement}</>}
              <Stack direction="row" spacing={2} alignItems="center">
                {showGlobalFilter && (
                  <>
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        title === 'Confidence Fields'
                          ? 'Click to  search  under field name, model status, recall and precision'
                          : 'Enter text to search across all columns in list view'
                      }
                    >
                      <Stack>
                        <GlobalFilter
                          preGlobalFilteredRows={preGlobalFilteredRows}
                          globalFilter={state.globalFilter}
                          setGlobalFilter={setGlobalFilter}
                          unSelectAllRows={unSelectAllRows}
                        />
                      </Stack>
                    </Tooltip>
                  </>
                )}
                {showColumnLevelFilter && (
                  <Tooltip placement="top" arrow title={'Click to display or hide search text box under each column header.'}>
                    <Stack>
                      <ColumnLevelFilterButton
                        showColumnLevelFilterInputBox={showColumnLevelFilterInputBox}
                        setShowColumnLevelFilterInputBox={setShowColumnLevelFilterInputBox}
                      />
                    </Stack>
                  </Tooltip>
                )}
                {showRefreshButton && (
                  <Tooltip placement="top" arrow title={'Refresh the contents in list view'}>
                    <Stack>
                      <TableRefereshButton onRefreshClick={onRefreshClick} />
                    </Stack>
                  </Tooltip>
                )}
                {showCustomFilterColumnButton && (
                  <>
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        title === 'Prediction List'
                          ? 'Filter the records present in list view based on its batch status'
                          : title === 'Assurance List'
                          ? 'Filter the records present in list view based on its files status'
                          : title === 'Model Training'
                          ? 'Filter the records present in list view based on its status'
                          : ''
                      }
                    >
                      <Stack>
                        <TableFilterByColumn setFilter={setFilter} uniqueValuesByColumns={uniqueValuesByColumns} />
                      </Stack>
                    </Tooltip>
                  </>
                )}
                {createButton}
                {subHeading && <Typography variant={'h6'}>{subHeading}</Typography>}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <Grid container>
          {loading ? (
            <TableSkeleton columns={columns} />
          ) : (
            <ScrollX sx={{ maxHeight: tableMaxHeight }} ref={scrollContainerRef}>
              <Wrapper>
                {children &&
                  children({
                    getTableProps,
                    getTableBodyProps,
                    headerGroups,
                    page, // Pass 'page' instead of 'rows'
                    prepareRow,
                    expandedColumns,
                    toggleColumn,
                    isMobile,
                    getVisibleItems,
                    tableRef,
                    emptyTableMessage: translate('pages.global.noRecordsFound'),
                    gotoPage,
                    pageCount,
                    pageIndex: state.pageIndex,
                    pageSize: state.pageSize,
                    setPageSize,
                    showColumnLevelFilterInputBox,
                    internalSelectedRowIds: state.selectedRowIds,
                    toggleAllRowsSelected,
                    floatDeleteButton,
                    floatButtonRowIndex
                  })}
              </Wrapper>
            </ScrollX>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default TableWrapper;
